import { useCallback, useMemo } from "react"
import { useParams } from "react-router-dom"
import { useApplicationRoles } from "../../../../../hooks/useApplicationRoles"
import { useDeleteConfirm } from "../../../../../hooks/useDeleteConfirm"
import { ApplicationRoleGet } from "../../../../models/ApplicationRole"
import { DimaFeatureShortNameEnum } from "../../../../models/Authorization"
import { deleteEntity } from "../../../../utils/deleteEntity"
import { booleanComparator, getDoneIcon, getEmptyIcon } from "../../../../utils/getBooleanFlagIcon"
import { DiMaAddButton } from "../../../partials/DiMaAddButton"
import { EntityRow, EntityTab } from "../../../partials/EntityTab"

export function ApplicationRoles() {
  const { applicationId } = useParams()
  const { requestDelete, DeleteConfirmDialog } = useDeleteConfirm()
  const { applicationRoles, isLoadingApplicationRoles } =
    useApplicationRoles(applicationId)

  const cols = [
    {
      field: "name",
      headerName: "Name",
      isLink: true,
      linkBasePath: `/applications/${applicationId}/roles`,
    },
    { field: "description", headerName: "Description" },
    {
      field: "isDefault",
      headerName: "Is Default",
      renderCell: (row: EntityRow) => row.isDefault ? getDoneIcon() : getEmptyIcon(),
      sortComparator: booleanComparator,
    },
    {
      field: "isRequestable",
      headerName: "Is Requestable",
      renderCell: (row: EntityRow) => row.isRequestable ? getDoneIcon() : getEmptyIcon(),
      sortComparator: booleanComparator,
    },
    { field: "priority", headerName: "Priority" },
  ]

  const sorted = [...applicationRoles].sort(
    (a: ApplicationRoleGet, b: ApplicationRoleGet) =>
      (a.priority ?? 0) - (b.priority ?? 0)
  )

  const rows = sorted.map((appRole: ApplicationRoleGet) => ({
    id: appRole.id,
    name: appRole.name,
    description: appRole.description,
    isDefault: appRole.isDefault,
    isRequestable: appRole.isRequestable,
    priority: appRole.priority,
  }))

  const toolbar = useMemo(
    () => (
      <DiMaAddButton
        url={`/applications/${applicationId}/roles/create`}
        feature={DimaFeatureShortNameEnum.AM}
      />
    ),
    [applicationId]
  )

  const handleActionMenuClick = useCallback((item: { title: string }, row: EntityRow) => {
      if (item.title === "Delete") {
        requestDelete(() =>
          deleteEntity(`/applications/${applicationId}/roles/${row.id}`)
        )
      }
    }, [applicationId, requestDelete])

  return (
    <>
      {DeleteConfirmDialog}
      <EntityTab
        rows={rows}
        cols={cols}
        isLoading={isLoadingApplicationRoles}
        toolbar={toolbar}
        actionItems={[{ title: "Delete", iconName: "icon_abb_dias icon-abb_delete" }]}
        onActionMenuClick={handleActionMenuClick}
      />
    </>
  )
}
